"use client";

import { cn } from "@/lib/utils";
import { MoveRight } from "lucide-react";
import Link from "next/link";
import type { Article } from "@/data/articles";

interface PopularArticlesProps {
  title?: string;
  description?: string;
  backgroundLabel?: string;
  backgroundPosition?: "left" | "right";
  articles: Article[];
  className?: string;
}

export function PopularArticles({
  title,
  description,
  backgroundLabel,
  backgroundPosition = "left",
  articles,
  className,
}: PopularArticlesProps) {
  return (
    <section
      className={cn("container relative mb-10 py-10 mx-auto px-4", className)}
    >
      {title && (
        <h1 className="text-center text-4xl font-semibold capitalize !leading-[1.4] md:text-5xl lg:text-6xl mb-2">
          {title}
        </h1>
      )}

      {backgroundLabel && (
        <span
          className={cn(
            "absolute -top-10 -z-50 select-none text-[180px] font-extrabold leading-[1] text-black/[0.03] md:text-[250px] lg:text-[400px] text-foreground/[0.025]",
            backgroundPosition === "left" ? "-left-[18%]" : "-right-[28%]"
          )}
        >
          {backgroundLabel}
        </span>
      )}

      {description && (
        <p className="mx-auto max-w-[800px] text-center text-xl !leading-[2] text-foreground/50 md:text-2xl mb-8">
          {description}
        </p>
      )}

      <div className="grid h-auto grid-cols-1 gap-5 md:h-[650px] md:grid-cols-2 lg:grid-cols-[1fr_0.5fr]">
        {articles.slice(0, 3).map((article, index) => {
          const isPrimary = index === 0;

          return (
            <Link
              key={article.id}
              href={article.href}
              style={{ backgroundImage: `url(${article.imageUrl})` }}
              className={cn(
                "group relative row-span-1 flex size-full cursor-pointer flex-col justify-end overflow-hidden rounded-[20px] bg-cover bg-center bg-no-repeat p-5 text-white max-md:h-[300px] transition-all duration-300 hover:scale-[0.98] hover:rotate-[0.3deg]",
                isPrimary &&
                  "col-span-1 row-span-1 md:col-span-2 md:row-span-2 lg:col-span-1"
              )}
            >
              <div className="absolute inset-0 -z-0 h-[130%] w-full bg-gradient-to-t from-black/80 to-transparent transition-all duration-500 group-hover:h-full" />

              <article className="relative z-0 flex items-end">
                <div className="flex flex-col flex-1 gap-3">
                  <h2 className="text-3xl font-semibold md:text-4xl">
                    {article.title}
                  </h2>
                  <div className="flex flex-col gap-3">
                    <span className="px-2 py-px text-base text-white capitalize rounded-md bg-white/40 w-fit backdrop-blur-md">
                      {article.category}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-thin">
                        {article.views} visningar
                      </span>
                      <span className="text-lg font-thin">•</span>
                      <span className="text-lg font-thin">
                        {article.readTime} min läsning
                      </span>
                    </div>
                  </div>
                </div>
                <MoveRight
                  className="transition-all duration-300 group-hover:translate-x-2"
                  color="white"
                  width={40}
                  height={40}
                  strokeWidth={1.25}
                />
              </article>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
